import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import BudgetActivationControl from "./BudgetActivationControl";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, Zap, DollarSign, TrendingUp, Play, SkipForward } from "lucide-react";

export default function PaidSweepQueuePanel() {
  const [items, setItems] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [budgetCap, setBudgetCap] = useState(10);
  const [activatingTop, setActivatingTop] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [pendingLabel, setPendingLabel] = useState(null);
  const [budgetActive, setBudgetActive] = useState(false);
  const [hardCap, setHardCap] = useState(0);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/paid-sweep-queue`);
      setItems(res.data.items || []);
      setTotalCost(res.data.total_estimated_cost || 0);
      setPendingLabel(res.data.pending_approval_label || null);
      setBudgetActive(Boolean(res.data.budget_active));
      setHardCap(Number(res.data.budget_cap || 0));
      setLastRefreshedAt(Date.now());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const activate = async (city, state) => {
    setActing(city);
    try {
      await axios.post(`${API}/admin/paid-sweep-queue/activate/${encodeURIComponent(city)}?state=${state}`);
      toast.success(`${city} activated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activation failed");
    } finally { setActing(null); }
  };

  const skip = async (city, state) => {
    setActing(city);
    try {
      await axios.post(`${API}/admin/paid-sweep-queue/skip/${encodeURIComponent(city)}?state=${state}`);
      toast.success(`${city} skipped`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Skip failed");
    } finally { setActing(null); }
  };

  const activateTop = async () => {
    setActivatingTop(true);
    try {
      const res = await axios.post(`${API}/admin/paid-sweep-queue/activate-top`, {
        budget_cap: budgetCap,
        max_cities: 10,
      });
      toast.success(`Activated ${res.data.count} cities ($${res.data.total_cost})`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch activation failed");
    } finally { setActivatingTop(false); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="paid-sweep-queue-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={load} lastRefreshedAt={lastRefreshedAt} testId="paid-sweep-refresh" /></div>

      {/* v1.0.0 Item 14 — PENDING YOUR APPROVAL banner. Rendered whenever budget is inactive. */}
      {pendingLabel && (
        <div
          className="rounded-lg border-2 border-amber-500 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-2.5 flex items-center gap-2.5"
          data-testid="paid-sweep-pending-approval-banner"
        >
          <span className="text-[10px] font-black tracking-wider bg-amber-500 text-white px-2 py-1 rounded uppercase">
            {pendingLabel}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-amber-900 leading-tight">
              No charge has occurred. Paid sweep budget is <span className="font-black">${hardCap.toFixed(2)} ({budgetActive ? "active" : "inactive"})</span>.
            </p>
            <p className="text-[10px] text-amber-700 leading-tight mt-0.5">
              Cities below are informational only — flagged for future paid activation. Activate budget via the two-step control.
            </p>
          </div>
        </div>
      )}

      {/* v1.0.0 Item 8 — Two-step budget activation control */}
      <BudgetActivationControl onChange={load} />

      {/* Summary bar */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold text-sky-700" data-testid="paid-queue-count">{items.length}</p>
          <p className="text-[10px] text-sky-500">Queued Cities</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold text-amber-700" data-testid="paid-queue-cost">${totalCost.toFixed(2)}</p>
          <p className="text-[10px] text-amber-500">Est. Total Cost</p>
        </div>
      </div>

      {/* Budget cap + activate top */}
      {items.length > 0 && (
        <div className="flex items-center gap-2" data-testid="budget-cap-controls">
          <label className="text-[11px] text-slate-500 font-medium">Budget cap:</label>
          <div className="flex items-center gap-1 bg-slate-50 border rounded-lg px-2 py-1">
            <DollarSign className="w-3 h-3 text-slate-400" />
            <input
              type="number"
              value={budgetCap}
              onChange={(e) => setBudgetCap(parseFloat(e.target.value) || 0)}
              className="w-16 text-xs bg-transparent outline-none"
              min="0"
              step="1"
              data-testid="budget-cap-input"
            />
          </div>
          <button
            onClick={activateTop}
            disabled={activatingTop}
            className="flex items-center gap-1 px-3 py-1.5 bg-sky-500 text-white text-[11px] font-semibold rounded-lg hover:bg-sky-600 transition disabled:opacity-50"
            data-testid="activate-top-btn"
          >
            {activatingTop ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Activate Top 10
          </button>
        </div>
      )}

      {/* Queue list */}
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No cities waiting for paid sweep activation.</p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {items.map((item) => (
            <div key={`${item.city}-${item.state}`}
              className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3"
              data-testid={`paid-queue-item-${item.city}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{item.city}, {item.state}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                    ${item.estimated_cost_usd?.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                  <span className="flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />~{item.estimated_new_discoveries} new</span>
                  <span>value: {item.value_score?.toFixed(0)}</span>
                  <span>queued {item.queued_at?.slice(0, 10)}</span>
                </div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => activate(item.city, item.state)} disabled={acting === item.city}
                  className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 hover:bg-sky-100 flex items-center justify-center transition"
                  data-testid={`activate-btn-${item.city}`}>
                  {acting === item.city ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => skip(item.city, item.state)} disabled={acting === item.city}
                  className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 hover:bg-slate-100 flex items-center justify-center transition"
                  data-testid={`skip-btn-${item.city}`}>
                  <SkipForward className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
